import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import {
  MULTI_VALUE_METRIC_CONFIG,
  multiValueMetricList,
  type IMultiValueMetricConfig,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/ConsoleData/_config';

type TFormatMessage = (descriptor: {
  id: string;
  defaultMessage?: string;
}) => string;

export const isMultiValueMetric = (metric = ''): boolean =>
  Boolean(MULTI_VALUE_METRIC_CONFIG[metric]);

const getMultiValueMetricConfig = (
  metric = '',
): IMultiValueMetricConfig | undefined => MULTI_VALUE_METRIC_CONFIG[metric];

const isSubMetricOf = (metricKey = '', baseMetric = ''): boolean => {
  if (!metricKey || !baseMetric) return false;
  if (metricKey === baseMetric) return true;
  return metricKey.startsWith(`${baseMetric}__`);
};

const sortSubMetricKeys = (baseMetric: string, keys: string[]): string[] =>
  [...keys].sort((a, b) =>
    a.slice(baseMetric.length).localeCompare(b.slice(baseMetric.length), undefined, {
      numeric: true,
    }),
  );

const collectSubMetricKeysFromTrends = (
  baseMetric: string,
  trendsData: any[] = [],
): string[] => {
  const keys = new Set<string>();
  trendsData.forEach((row) => {
    (row?.metrics || []).forEach((m: any) => {
      const key = m?.metric || m?.name;
      if (key && isSubMetricOf(key, baseMetric) && key !== baseMetric) {
        keys.add(key);
      }
    });
  });
  return sortSubMetricKeys(baseMetric, Array.from(keys));
};

const collectSubMetricKeysFromDownloadRows = (
  baseMetric: string,
  rows: any[] = [],
): string[] => {
  const keys = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((fieldKey) => {
      if (!fieldKey.startsWith('metrics_')) return;
      const metricPart = fieldKey.replace(/^metrics_/, '').replace(/_growth$/, '');
      const subKey = metricPart.split('_segment_')[0];
      if (subKey && isSubMetricOf(subKey, baseMetric) && subKey !== baseMetric) {
        keys.add(subKey);
      }
    });
  });
  return sortSubMetricKeys(baseMetric, Array.from(keys));
};

const resolveMultiValueBaseMetric = (metric = ''): string | undefined =>
  multiValueMetricList.find((base) => isSubMetricOf(metric, base));

export const isMultiValuePriceMetric = (metric = ''): boolean =>
  isMultiValueMetric(metric) || Boolean(resolveMultiValueBaseMetric(metric));

const extractSubMetricSuffix = (subKey = '', baseMetric = ''): string => {
  if (!subKey || !baseMetric || subKey === baseMetric) return '';
  const prefix = `${baseMetric}__`;
  if (!subKey.startsWith(prefix)) return '';
  return subKey.slice(prefix.length);
};

const formatMultiValueMetricTitle = (
  subKey: string,
  baseMetric: string,
  formatMessage?: TFormatMessage,
): string => {
  const config = MULTI_VALUE_METRIC_CONFIG[baseMetric];
  if (!config) return subKey;
  const suffix = extractSubMetricSuffix(subKey, baseMetric);
  const prefix = formatMessage
    ? formatMessage({
        id: config.columnTitlePrefix,
        defaultMessage: config.downloadTitlePrefix,
      })
    : config.downloadTitlePrefix;
  return suffix ? `${prefix}-${suffix}` : prefix;
};

export const getMetricDisplayText = (
  metricObj: any,
  formatMessage?: TFormatMessage,
): string => {
  if (metricObj?.textStr) return metricObj.textStr;
  if (metricObj?.text && !String(metricObj.text).startsWith('dbi_')) {
    return metricObj.text;
  }
  if (metricObj?.text && formatMessage) {
    return formatMessage({
      id: metricObj.text,
      defaultMessage: metricObj.text,
    });
  }
  return metricObj?.text || '';
};

export const findMetricInTableList = (
  metricId = '',
  tableMetricList: any[] = [],
): any | undefined =>
  tableMetricList.find((item) => item?.value === metricId) ||
  tableMetricList.find((item) =>
    isMultiValueMetric(metricId)
      ? resolveMultiValueBaseMetric(item?.value) === metricId ||
        item?.value?.startsWith(`${metricId}__`)
      : false,
  );

const formatAxisMetricText = (
  metricId: string,
  options: {
    formatMessage: TFormatMessage;
    tableMetricList?: any[];
    formatLegendText?: (metricObj: any) => string;
  },
): string => {
  const { formatMessage, tableMetricList = [], formatLegendText } = options;
  const multiValueConfig = getMultiValueMetricConfig(metricId);
  if (multiValueConfig) {
    return formatMessage({
      id: multiValueConfig.columnTitlePrefix,
      defaultMessage: multiValueConfig.downloadTitlePrefix,
    });
  }
  const metricObj = findMetricInTableList(metricId, tableMetricList);
  if (metricObj && formatLegendText) return formatLegendText(metricObj);
  if (metricObj) return getMetricDisplayText(metricObj, formatMessage);
  return metricId;
};

/** Y 轴标题：仅使用用户选中的 base 指标，不展开子指标 */
export const buildVisualMetricAxisInfo = (
  metricIds: string[] = [],
  options: {
    tableMetricList?: any[];
    formatMessage: TFormatMessage;
    formatLegendText?: (metricObj: any) => string;
    formatAxisText?: (metricId: string, text: string, metricObj?: any) => string;
  },
): any[] => {
  const {
    tableMetricList = [],
    formatMessage,
    formatLegendText,
    formatAxisText,
  } = options;

  return metricIds
    .slice(0, 2)
    .map((metricId) => {
      const metricObj = findMetricInTableList(metricId, tableMetricList);
      const multiValueConfig = getMultiValueMetricConfig(metricId);
      if (!metricObj && !multiValueConfig && !resolveMultiValueBaseMetric(metricId)) {
        return null;
      }
      const text = formatAxisMetricText(metricId, {
        formatMessage,
        tableMetricList,
        formatLegendText,
      });
      return {
        value: metricId,
        text: formatAxisText ? formatAxisText(metricId, text, metricObj) : text,
        renderType: metricObj?.renderType,
        dataSource: metricObj?.dataSource,
      };
    })
    .filter(Boolean);
};

export const getDualAxisHeaderTexts = (axisItems: any[] = []): string[] => {
  const texts = axisItems
    .slice(0, 2)
    .map((item) => item?.text)
    .filter(Boolean);
  return texts.length === 2 ? texts : [];
};

const buildExpandedMetricOption = (
  opt: any,
  subKey: string,
  baseMetric: string,
  formatMessage?: TFormatMessage,
) => {
  const title = formatMultiValueMetricTitle(subKey, baseMetric, formatMessage);
  return {
    ...opt,
    value: subKey,
    text: title,
    textStr: title,
    textLocaleId: opt?.text,
    renderType: opt?.renderType || RenderType.STEAM_PRICE,
  };
};

export const expandMetricOptionsWithMultiValue = (
  metricOptions: any[] = [],
  trendsData: any[] = [],
  formatMessage?: TFormatMessage,
): any[] => {
  const result: any[] = [];
  metricOptions.forEach((opt) => {
    const baseValue = opt?.value;
    if (!isMultiValueMetric(baseValue)) {
      result.push(opt);
      return;
    }
    const subKeys = collectSubMetricKeysFromTrends(baseValue, trendsData);
    if (!subKeys.length) {
      result.push(opt);
      return;
    }
    subKeys.forEach((subKey) => {
      result.push(
        buildExpandedMetricOption(opt, subKey, baseValue, formatMessage),
      );
    });
  });
  return result;
};

export const expandVisualMetricSeriesItems = (
  visualMetricItems: any[] = [],
  trendsData: any[] = [],
  formatMessage?: TFormatMessage,
): any[] => {
  const result: any[] = [];
  visualMetricItems.forEach((item) => {
    const value = item?.value;
    if (!value) return;

    const baseMetric = isMultiValueMetric(value)
      ? value
      : resolveMultiValueBaseMetric(value);

    if (baseMetric && value !== baseMetric) {
      const title = formatMultiValueMetricTitle(value, baseMetric, formatMessage);
      result.push({
        ...item,
        text: title,
        textStr: title,
      });
      return;
    }

    if (baseMetric) {
      const subKeys = collectSubMetricKeysFromTrends(baseMetric, trendsData);
      if (subKeys.length) {
        subKeys.forEach((subKey) => {
          result.push(
            buildExpandedMetricOption(item, subKey, baseMetric, formatMessage),
          );
        });
        return;
      }
    }

    result.push(item);
  });
  return result;
};

export const hasMultiValueMetricDataInRow = (
  row: any,
  baseMetric: string,
  getMetricValueOfCvm: (cvm: any, metricKey: string) => any,
): boolean => {
  const subKeys = collectSubMetricKeysFromTrends(baseMetric, [row]);
  if (!subKeys.length) {
    const metricObj = getMetricValueOfCvm?.(row, baseMetric);
    return Boolean(metricObj?.value);
  }
  return subKeys.some((subKey) => {
    const metricObj = getMetricValueOfCvm?.(row, subKey);
    return Boolean(metricObj?.value);
  });
};

export const expandMultiValueMetricsForDownloadSummary = (
  metricKeys: string[] = [],
  rows: any[] = [],
): string[] => {
  const result: string[] = [];
  metricKeys.forEach((metricKey) => {
    if (!isMultiValueMetric(metricKey)) {
      result.push(metricKey);
      return;
    }
    const subKeys = collectSubMetricKeysFromDownloadRows(metricKey, rows);
    if (!subKeys.length) {
      result.push(metricKey);
      return;
    }
    subKeys.forEach((subKey) => result.push(subKey));
  });
  return result;
};

export const getMultiValueMetricDownloadTitle = (metricKey: string): string => {
  const baseMetric = resolveMultiValueBaseMetric(metricKey) || metricKey;
  const config = MULTI_VALUE_METRIC_CONFIG[baseMetric];
  if (!config) return metricKey;
  if (metricKey === baseMetric) return config.downloadTitlePrefix;
  return formatMultiValueMetricTitle(metricKey, baseMetric);
};

export const seriesBelongsToAxisMetric = (
  seriesName = '',
  seriesId = '',
  axisItem: any,
): boolean => {
  if (!axisItem) return false;
  const axisValue = axisItem?.value;
  const axisText = axisItem?.text;

  if (seriesId) {
    if (seriesId === axisValue) return true;
    if (axisValue && isSubMetricOf(seriesId, axisValue)) return true;
  }
  if (seriesName && axisText && seriesName === axisText) return true;
  if (axisValue && isMultiValueMetric(axisValue) && axisText && seriesName) {
    return seriesName === axisText || seriesName.startsWith(`${axisText}-`);
  }
  return false;
};

export const assignDistinctSeriesColors = (
  seriesList: any[] = [],
  colorPalette: string[] = ECHARTSCOLOR,
): any[] =>
  seriesList.map((series, index) => {
    const color =
      series?.color ||
      series?.itemStyle?.color ||
      series?.lineStyle?.color ||
      colorPalette[index % colorPalette.length];
    return {
      ...series,
      color,
      itemStyle: {
        ...(series?.itemStyle || {}),
        color,
      },
      lineStyle: {
        ...(series?.lineStyle || {}),
        color,
      },
    };
  });

/** 主机数据等多值指标折线：复用 AllMetrics formatter 展开子指标 */
export const createMultiValueLineSeriesFormatter = (options: {
  metric: string;
  infoItem?: any;
  trendLineList?: any[];
  formatMessage?: TFormatMessage;
  defaultFormatter: (params: any) => any;
  multiMetricFormatter: (params: any) => any;
}) => {
  const {
    metric,
    infoItem,
    trendLineList = [],
    formatMessage,
    defaultFormatter,
    multiMetricFormatter,
  } = options;

  return (params: any) => {
    if (!isMultiValueMetric(metric) || !infoItem) {
      return defaultFormatter(params);
    }
    const expandedItems = expandVisualMetricSeriesItems(
      [
        {
          value: metric,
          text: infoItem.text,
          renderType: infoItem.renderType,
        },
      ],
      params?.list || trendLineList,
      formatMessage,
    );
    return multiMetricFormatter({
      ...params,
      visuailsMetricValues: expandedItems,
      formatMessage,
    });
  };
};

const pickDownloadMetricValue = (item: any, subKey: string): string | number => {
  const direct = item?.[`metrics_${subKey}`];
  const segAll = item?.[`metrics_${subKey}_segment_All`];
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  if (segAll !== undefined && segAll !== null && segAll !== '') return segAll;
  return direct ?? segAll ?? '';
};

const pickDownloadMetricGrowth = (item: any, subKey: string): string | number => {
  const direct = item?.[`metrics_${subKey}_growth`];
  const segAll = item?.[`metrics_${subKey}_segment_All_growth`];
  if (direct !== undefined && direct !== null && direct !== '') return direct;
  if (segAll !== undefined && segAll !== null && segAll !== '') return segAll;
  return direct ?? segAll ?? '';
};

export const buildMultiValuePriceDownloadColumns = (
  metricsCache: Record<string, boolean>,
  targetList: any[] = [],
  isRatioShow = false,
  baseMetricFilter?: string,
): any[] => {
  const columns: any[] = [];
  const baseMetrics = baseMetricFilter
    ? [baseMetricFilter]
    : multiValueMetricList;

  baseMetrics.forEach((baseMetric) => {
    if (!metricsCache[baseMetric]) return;

    const subKeys = collectSubMetricKeysFromDownloadRows(baseMetric, targetList);
    const metricKeys = subKeys.length ? subKeys : [baseMetric];

    metricKeys.forEach((subKey) => {
      const title =
        subKey === baseMetric
          ? getMultiValueMetricDownloadTitle(baseMetric)
          : formatMultiValueMetricTitle(subKey, baseMetric);

      columns.push({
        title,
        key: (item: any) => pickDownloadMetricValue(item, subKey),
        when: () => metricsCache[baseMetric],
      });

      if (isRatioShow) {
        columns.push({
          title: '%',
          key: (item: any) => pickDownloadMetricGrowth(item, subKey),
          when: () => metricsCache[baseMetric] && isRatioShow,
        });
      }
    });
  });

  return columns;
};
